import Link from 'next/link';

import { NAV_LINKS } from '../config/marketing-nav';

/**
 * SSR-crawlable mirror of the hover-only nav popover links.
 *
 * CONSTRAINT: the desktop nav's sub-links (product stages, use cases,
 * resources) only mount inside MarketingNavLinks' AnimatePresence popover,
 * which starts closed - and the mobile drawer only mounts when opened. So none
 * of those hrefs exist in the server-rendered HTML, and a crawler that does not
 * simulate hover never sees them from the header.
 *
 * This renders the same set as a real, semantic `sr-only` list so the links are
 * in the SSR markup and followable, without touching the popover animation.
 * It reads NAV_LINKS - the single config both popovers and the mobile drawer
 * read - so it can never drift from the visible nav.
 *
 * Visually hidden (`sr-only`), and `aria-hidden` + `tabIndex={-1}` so screen
 * readers and keyboard users get the real nav rather than a duplicate list.
 */
export const MarketingNavCrawlLinks = () => {
	const seen = new Set<string>();
	const entries: { href: string; label: string }[] = [];

	for (const link of NAV_LINKS) {
		const candidates = [
			...(link.href ? [{ href: link.href, label: link.label }] : []),
			...(link.items ?? []).map((item) => ({ href: item.href, label: item.label })),
		];
		for (const candidate of candidates) {
			// Internal routes only - external panel targets are not ours to index.
			if (!candidate.href.startsWith('/') || seen.has(candidate.href)) continue;
			seen.add(candidate.href);
			entries.push(candidate);
		}
	}

	return (
		<ul className="sr-only" aria-hidden>
			{entries.map(({ href, label }) => (
				<li key={href}>
					<Link href={href} tabIndex={-1}>
						{label}
					</Link>
				</li>
			))}
		</ul>
	);
};
